import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import MiniLineChart from "./MiniLineChart";
import { sel } from "./configuratorMetrics";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
const GRAN = [
  ["saptamani", "Saptamani"],
  ["luni", "Luni"],
  ["trimestre", "Trimestre"],
  ["semestre", "Semestre"],
];
const SERIES = [
  ["comenzi", "Comenzi unice", "#3b82f6"],
  ["validari", "Validari", "#8b5cf6"],
  ["pret_fab_eur", "Total EUR (pret fabrica)", "#10b981"],
];
const fmt = (n) => Number(n || 0).toLocaleString("ro-RO", { maximumFractionDigits: 0 });

export default function ProductieEvolutiv() {
  const [f, setF] = useState({ an: CURRENT_YEAR, granularitate: "luni" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/productie/evolutiv", { params: f });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.perioade || [];
  const labels = rows.map((r) => r.label);

  return (
    <div data-testid="productie-evolutiv-panel" className="space-y-4">
      <div className="rounded border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-medium text-slate-600">An
          <select value={f.an} onChange={(e) => setF({ ...f, an: Number(e.target.value) })} className={`mt-1 block ${sel}`} data-testid="productie-evolutiv-an">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">Perioada
          <select value={f.granularitate} onChange={(e) => setF({ ...f, granularitate: e.target.value })} className={`mt-1 block ${sel}`} data-testid="productie-evolutiv-granularitate">
            {GRAN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        {loading ? <Loader2 size={16} className="animate-spin text-slate-400" data-testid="productie-evolutiv-loading" /> : null}
      </div>
      {error ? <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="productie-evolutiv-error">{error}</div> : null}
      {data ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {SERIES.map(([k, l, c]) => (
              <div key={k} className="rounded border border-slate-200 bg-white p-4" data-testid={`productie-evolutiv-chart-${k}`}>
                <div className="mb-2 flex justify-between text-sm">
                  <span className="font-semibold text-slate-700">{l}</span>
                  <span className="text-slate-500">Total: <b className="text-slate-800" data-testid={`productie-evolutiv-total-${k}`}>{fmt(data.total[k])}</b></span>
                </div>
                <MiniLineChart labels={labels} values={rows.map((r) => r[k])} color={c} />
              </div>
            ))}
          </div>
          <div className="rounded border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="productie-evolutiv-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Perioada</th>
                  <th className="px-3 py-2 text-left">Interval</th>
                  {SERIES.map(([k, l]) => <th key={k} className="px-3 py-2 text-right">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className="border-t border-slate-100" data-testid={`productie-evolutiv-row-${r.label}`}>
                    <td className="px-3 py-1.5 font-medium">{r.label}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.start} - {r.end}</td>
                    {SERIES.map(([k]) => <td key={k} className="px-3 py-1.5 text-right">{fmt(r[k])}</td>)}
                  </tr>
                ))}
                <tr className="border-t border-slate-300 bg-slate-50 font-semibold">
                  <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                  {SERIES.map(([k]) => <td key={k} className="px-3 py-2 text-right">{fmt(data.total[k])}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
